import { useCallback, useEffect, useMemo, useState } from "react";
import {
  cachePreview,
  cacheRemoteSummaries,
  getLocalActivity,
  listCachedRemoteSummaries,
  listLocalActivitySummaries,
  listLocalBookmarks,
  loadPreview,
  localTrack,
  savedIdsFromLocal,
  setLocalBookmark,
} from "./local";
import { getRemoteTracks, listRemoteActivities, refreshBookmarks, sendBookmarkChanges } from "./remote";
import {
  crearCapa,
  extraerLinea,
  fechaEcuador,
  formatDuration,
  salidasGuardadas,
  simplificarLinea,
  sumarSalidas,
  unificarSalidas,
  type Coordenada,
  type Periodo,
  type Salida,
} from "./model";
import type { MiMapaMapRenderer, MiMapaRegion } from "./map-contract";
import "./mi-mapa.css";

type Props = {
  ownerId: string;
  accessAllowed: boolean;
  online: boolean;
  isAndroidApp: boolean;
  mapFile: string;
  mapCenter: [number, number];
  mapZoom?: number;
  regions?: MiMapaRegion[];
  selectedRegion?: string;
  onRegionChange?: (id: string) => void;
  routeNames?: Record<string, string>;
  renderMap: MiMapaMapRenderer;
  onClose: () => void;
};

const periodos: Array<{ value: Periodo; label: string }> = [
  { value: "semana", label: "Semana" },
  { value: "mes", label: "Mes" },
  { value: "anio", label: "Año" },
  { value: "todo", label: "Todo" },
];

export function MiMapaPage({
  ownerId,
  accessAllowed,
  online,
  isAndroidApp,
  mapFile,
  mapCenter,
  mapZoom = 11,
  regions = [],
  selectedRegion,
  onRegionChange,
  routeNames = {},
  renderMap,
  onClose,
}: Props) {
  const [periodo, setPeriodo] = useState<Periodo>("mes");
  const [view, setView] = useState<"mapa" | "progreso">("mapa");
  const [activities, setActivities] = useState<Salida[]>([]);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(false);
  const [cloudComplete, setCloudComplete] = useState(false);
  const [notice, setNotice] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [visibleTracks, setVisibleTracks] = useState(40);
  const [visibleList, setVisibleList] = useState(30);
  const [visiblePrevious, setVisiblePrevious] = useState(30);
  const [lines, setLines] = useState<Array<{ salida: Salida; line: Coordenada[] }>>([]);
  const [geometryBusy, setGeometryBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!accessAllowed || !ownerId) {
      setActivities([]);
      setSavedIds(new Set());
      setLoaded(true);
      setCloudComplete(false);
      setNotice("Mi mapa no esta habilitado para este perfil.");
      return;
    }

    setLoaded(false);
    setCloudComplete(false);
    try {
      const [local, cachedRemote, localBookmarks] = await Promise.all([
        listLocalActivitySummaries(ownerId),
        listCachedRemoteSummaries(ownerId),
        listLocalBookmarks(ownerId),
      ]);
      setActivities(unificarSalidas(ownerId, local, cachedRemote));
      setSavedIds(savedIdsFromLocal(localBookmarks));
      setLoaded(true);

      if (!online) {
        setNotice("Sin conexion: resultados disponibles en este dispositivo.");
        return;
      }

      const [cloudActivities, cloudBookmarks] = await Promise.allSettled([
        listRemoteActivities(ownerId),
        refreshBookmarks(ownerId),
      ]);

      if (cloudActivities.status === "fulfilled") {
        setActivities(unificarSalidas(ownerId, local, cloudActivities.value));
        await cacheRemoteSummaries(ownerId, cloudActivities.value).catch(() => undefined);
      }
      if (cloudBookmarks.status === "fulfilled") setSavedIds(cloudBookmarks.value.saved);

      if (cloudActivities.status === "fulfilled" && cloudBookmarks.status === "fulfilled") {
        setCloudComplete(true);
        if (cloudBookmarks.value.sync.conflicts > 0) {
          setNotice("Otro dispositivo tenia cambios mas recientes en algunas salidas. Se conservo el estado remoto y puedes elegir de nuevo si lo deseas.");
        } else if (cloudBookmarks.value.sync.failed > 0 || cloudBookmarks.value.sync.pending > 0) {
          setNotice("Algunos cambios de Mi mapa siguen pendientes y se reintentaran despues.");
        } else {
          setNotice("");
        }
      } else {
        setCloudComplete(false);
        setNotice("La nube no respondio por completo; se muestra lo disponible en este dispositivo.");
      }
    } catch (error) {
      setLoaded(true);
      setNotice(error instanceof Error ? error.message : "No se pudo leer Mi mapa.");
    }
  }, [accessAllowed, ownerId, online]);

  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => {
    // THERA_MI_MAPA_ACCOUNT_REFRESH_V1
    // Una cuenta = un Mi mapa.
    // Web, PWA, Android y futuros WebViews iOS vuelven a consultar
    // el estado de la cuenta cuando recuperan el primer plano.
    if (!accessAllowed || !ownerId || !online) return;

    let lastRefreshAt = Date.now();

    const refreshAccountMap = () => {
      if (document.visibilityState === "hidden") return;

      const now = Date.now();

      // Evita llamadas duplicadas cuando focus + visibilitychange
      // se disparan juntos.
      if (now - lastRefreshAt < 1500) return;

      lastRefreshAt = now;
      void reload();
    };

    window.addEventListener("focus", refreshAccountMap);
    window.addEventListener("pageshow", refreshAccountMap);
    document.addEventListener("visibilitychange", refreshAccountMap);

    return () => {
      window.removeEventListener("focus", refreshAccountMap);
      window.removeEventListener("pageshow", refreshAccountMap);
      document.removeEventListener("visibilitychange", refreshAccountMap);
    };
  }, [accessAllowed, online, ownerId, reload]);

  useEffect(() => {
    setVisibleTracks(40);
    setVisibleList(30);
    setVisiblePrevious(30);
    setSelected(null);
    setLines([]);
    setGeometryBusy(false);
  }, [periodo, ownerId]);

  const saved = useMemo(() => salidasGuardadas(activities, savedIds, periodo), [activities, savedIds, periodo]);
  const totals = useMemo(() => sumarSalidas(saved), [saved]);
  const candidates = useMemo(() => activities.filter((item) => !savedIds.has(item.id)), [activities, savedIds]);
  const drawnCandidates = useMemo(() => saved.slice(0, visibleTracks), [saved, visibleTracks]);

  useEffect(() => {
    if (view !== "mapa" || !accessAllowed) {
      setGeometryBusy(false);
      return;
    }
    let cancelled = false;
    setLines([]);
    setGeometryBusy(true);

    const load = async () => {
      const results = await Promise.all(drawnCandidates.map(async (salida) => {
        let line = await loadPreview(ownerId, salida.id).catch(() => null);
        if (!line) {
          const local = await getLocalActivity(ownerId, salida.id).catch(() => null);
          if (local) line = extraerLinea(await localTrack(ownerId, salida.id));
        }
        return { salida, line: line ?? [] };
      }));

      const missing = results.filter(({ salida, line }) => line.length < 2 && Boolean(salida.remoteId));
      if (online && missing.length) {
        try {
          const tracks = await getRemoteTracks(ownerId, missing.map(({ salida }) => salida.remoteId!));
          for (const item of missing) item.line = extraerLinea(tracks.get(item.salida.remoteId!));
        } catch {
          if (!cancelled) setNotice("Algunos trazados no pudieron cargarse. Las cifras guardadas siguen disponibles.");
        }
      }

      for (const item of results) {
        if (item.line.length >= 2) {
          item.line = simplificarLinea(item.line);
          await cachePreview(ownerId, item.salida.id, item.line).catch(() => undefined);
        }
      }

      if (!cancelled) {
        setLines(results);
        setGeometryBusy(false);
      }
    };

    void load().catch(() => {
      if (!cancelled) {
        setLines([]);
        setGeometryBusy(false);
        setNotice("No se pudieron dibujar algunos recorridos.");
      }
    });

    return () => { cancelled = true; };
  }, [accessAllowed, drawnCandidates, online, ownerId, view]);

  const overlay = useMemo(() => crearCapa(lines, selected, saved), [lines, selected, saved]);
  const focusLine = lines.find((item) => item.salida.id === selected)?.line;
  const focusBounds = focusLine && focusLine.length >= 2 ? {
    west: Math.min(...focusLine.map((p) => p[0])),
    south: Math.min(...focusLine.map((p) => p[1])),
    east: Math.max(...focusLine.map((p) => p[0])),
    north: Math.max(...focusLine.map((p) => p[1])),
  } : undefined;

  const syncLocalSavedIds = async () => {
    const local = await listLocalBookmarks(ownerId);
    setSavedIds(savedIdsFromLocal(local));
  };

  const changeSaved = async (item: Salida, nextSaved: boolean) => {
    if (!accessAllowed) return;
    setSavingId(item.id);
    let localSaved = false;
    try {
      await setLocalBookmark(ownerId, item.id, nextSaved);
      localSaved = true;
      await syncLocalSavedIds();
      setNotice(online
        ? "Cambio guardado en este dispositivo; sincronizando..."
        : "Cambio guardado sin conexion; se sincronizara al volver la red.");

      if (online) {
        const sync = await sendBookmarkChanges(ownerId);
        await syncLocalSavedIds();
        const outcome = sync.outcomes[item.id];
        if (outcome === "conflict-remote-wins") {
          setNotice("Otro dispositivo cambio esta salida despues de tu ultima sincronizacion. Se conservo el estado remoto; puedes elegir de nuevo si corresponde.");
        } else if (outcome === "activity-pending") {
          setNotice("La salida aun no termino de sincronizarse con tu cuenta. Tu eleccion queda guardada y se reintentara.");
        } else if (outcome === "failed") {
          setNotice("Tu eleccion queda guardada en este dispositivo y se reintentara al recuperar conexion.");
        } else {
          setNotice(nextSaved ? "Salida guardada en Mi mapa." : "Salida retirada de Mi mapa.");
        }
      }
    } catch {
      setNotice(localSaved
        ? "Cambio pendiente de sincronizar con tu cuenta."
        : "No se pudo guardar el cambio en este dispositivo.");
    } finally {
      setSavingId(null);
    }
  };

  if (!accessAllowed) {
    return (
      <section className="mi-mapa" aria-label="Mi mapa personal">
        <header className="mi-mapa-header">
          <div><small>MIS RUTAS · PRIVADO</small><h2>Mi mapa</h2></div>
          <button type="button" onClick={onClose}>Cerrar</button>
        </header>
        <p className="mi-mapa-notice" role="status">Mi mapa no esta habilitado para este perfil.</p>
      </section>
    );
  }

  return (
    <section className={`mi-mapa ${isAndroidApp ? "mi-mapa-android" : "mi-mapa-web"}`} aria-label="Mi mapa personal">
      <header className="mi-mapa-header">
        <div><small>MIS RUTAS · PRIVADO</small><h2>Mi mapa</h2><p>Tus salidas guardadas, un recorrido a la vez.</p></div>
        <button type="button" onClick={onClose} aria-label="Cerrar Mi mapa">Cerrar</button>
      </header>

          {/* THERA ANDROID MI MAPA COMPACT V2 EXACT: solo presentacion Android. */}
    {isAndroidApp ? (
      <div className="mi-mapa-android-compact-controls" aria-label="Controles de Mi mapa">
        <details
          className="mi-mapa-android-dropdown"
          onClick={(event) => {
            if ((event.target as HTMLElement).closest("button")) event.currentTarget.open = false;
          }}
        >
          <summary>
            <span>Vista: {view === "mapa" ? "Mapa" : "Progreso"}</span>
            <span className="mi-mapa-android-chevron" aria-hidden="true" />
          </summary>
          <nav className="mi-mapa-nav" aria-label="Seccion de Mi mapa">
                  <button type="button" className={view === "mapa" ? "selected" : ""} onClick={() => setView("mapa")}>Mapa</button>
                  <button type="button" className={view === "progreso" ? "selected" : ""} onClick={() => setView("progreso")}>Progreso</button>
                </nav>
        </details>
        <details
          className="mi-mapa-android-dropdown"
          onClick={(event) => {
            if ((event.target as HTMLElement).closest("button")) event.currentTarget.open = false;
          }}
        >
          <summary>
            <span>Periodo: {periodos.find((item) => item.value === periodo)?.label ?? "Mes"}</span>
            <span className="mi-mapa-android-chevron" aria-hidden="true" />
          </summary>
          <div className="mi-mapa-periodos" role="group" aria-label="Periodo de salidas guardadas">
                  {periodos.map((item) => (
                    <button key={item.value} type="button" aria-pressed={periodo === item.value}
                      className={periodo === item.value ? "selected" : ""} onClick={() => setPeriodo(item.value)}>{item.label}</button>
                  ))}
                </div>
        </details>
              <details className="mi-mapa-android-dropdown mi-mapa-android-data-dropdown">
          <summary>
            <span>Datos</span>
            <span className="mi-mapa-android-chevron" aria-hidden="true" />
          </summary>
          <div className="mi-mapa-android-data-panel">

          {notice && <p className="mi-mapa-notice" role="status">{notice}</p>}
          {!loaded && <p role="status">Leyendo salidas guardadas...</p>}
          {loaded && <p className="mi-mapa-scope">{cloudComplete ? "Cuenta sincronizada" : "Datos disponibles en este dispositivo"} · Solo cuentan las salidas que guardaste en Mi mapa.</p>}

          <div className="mi-mapa-metrics" aria-label="Resumen del periodo">
          <div><strong>{(totals.distanceM / 1000).toLocaleString("es-EC", { maximumFractionDigits: 1 })}</strong><small>km</small></div>
          <div><strong>{totals.salidas}</strong><small>salidas</small></div>
          <div><strong>{formatDuration(totals.durationS)}</strong><small>tiempo</small></div>
          <div><strong>+{Math.round(totals.ascentM).toLocaleString("es-EC")}</strong><small>m subida</small></div>
          <div><strong>{totals.activeDays}</strong><small>dias activos</small></div>
          </div>


          </div>
        </details>
      </div>
    ) : (
      <>
        <nav className="mi-mapa-nav" aria-label="Seccion de Mi mapa">
                <button type="button" className={view === "mapa" ? "selected" : ""} onClick={() => setView("mapa")}>Mapa</button>
                <button type="button" className={view === "progreso" ? "selected" : ""} onClick={() => setView("progreso")}>Progreso</button>
              </nav>
        <div className="mi-mapa-periodos" role="group" aria-label="Periodo de salidas guardadas">
                {periodos.map((item) => (
                  <button key={item.value} type="button" aria-pressed={periodo === item.value}
                    className={periodo === item.value ? "selected" : ""} onClick={() => setPeriodo(item.value)}>{item.label}</button>
                ))}
              </div>
      </>
    )}

      {/* THERA MI MAPA TEXT FOLDS V5.5E: solo texto/metricas */}
          {/* THERA ANDROID MI MAPA CONTROLS V3 SAFE: Web/PWA conservan su fold original. */}
    {!isAndroidApp && (
      <details className="mi-mapa-text-fold mi-mapa-summary-text-fold" open>
              <summary className="mi-mapa-text-fold-button">
                <span><strong>Resumen</strong><small>Metricas e informacion</small></span>
                <span className="mi-mapa-text-fold-open" aria-hidden="true">Contraer</span>
                <span className="mi-mapa-text-fold-closed" aria-hidden="true">Desplegar</span>
              </summary>
            {notice && <p className="mi-mapa-notice" role="status">{notice}</p>}
            {!loaded && <p role="status">Leyendo salidas guardadas...</p>}
            {loaded && <p className="mi-mapa-scope">{cloudComplete ? "Cuenta sincronizada" : "Datos disponibles en este dispositivo"} · Solo cuentan las salidas que guardaste en Mi mapa.</p>}

            <div className="mi-mapa-metrics" aria-label="Resumen del periodo">
              <div><strong>{(totals.distanceM / 1000).toLocaleString("es-EC", { maximumFractionDigits: 1 })}</strong><small>km</small></div>
              <div><strong>{totals.salidas}</strong><small>salidas</small></div>
              <div><strong>{formatDuration(totals.durationS)}</strong><small>tiempo</small></div>
              <div><strong>+{Math.round(totals.ascentM).toLocaleString("es-EC")}</strong><small>m subida</small></div>
              <div><strong>{totals.activeDays}</strong><small>dias activos</small></div>
            </div>

            </details>
    )}

      <div className="mi-mapa-body">
        {view === "mapa" && <div className="mi-mapa-map" aria-label="Mis recorridos sobre el mapa THERA">
          {isAndroidApp && regions.length > 0 && <label className="mi-mapa-region">Region del mapa
            <select value={selectedRegion} onChange={(event: { target: { value: string } }) => onRegionChange?.(event.target.value)}>
              {regions.map((region) => <option key={region.id} value={region.id}>{region.name}</option>)}
            </select>
          </label>}

          {renderMap({
            isAndroidApp,
            mapFile,
            mapCenter,
            mapZoom,
            overlay,
            focusBounds,
            onTrackSelect: setSelected,
          })}

          <div className="mi-mapa-map-foot" role="status">
            {geometryBusy ? "Dibujando recorridos..." : `${overlay.features.length} de ${saved.length} salidas del periodo dibujadas`}
            {saved.length > visibleTracks && <button type="button" onClick={() => setVisibleTracks((count) => count + 40)}>Mostrar 40 mas</button>}
          </div>
        </div>}

        <aside className="mi-mapa-list">
        {/* THERA MI MAPA TEXT FOLDS V5.5E: solo lista de recorridos */}
        <details className="mi-mapa-text-fold mi-mapa-tracks-text-fold" open>
          <summary className="mi-mapa-text-fold-button">
            <span><strong>Mis recorridos</strong><small>Salidas guardadas</small></span>
            <span className="mi-mapa-text-fold-open" aria-hidden="true">Contraer</span>
            <span className="mi-mapa-text-fold-closed" aria-hidden="true">Desplegar</span>
          </summary>
          <h3>{view === "mapa" ? "Mis recorridos" : "Progreso personal"}</h3>
          {totals.withoutTrack > 0 && <p>{totals.withoutTrack} salidas guardadas tienen menos de dos puntos GPS; cuentan en tus cifras, sin linea en el mapa.</p>}
          {saved.length === 0 && <p>Aun no guardaste salidas en este periodo. Finaliza una ruta y elige "Guardar en Mi mapa".</p>}

          {saved.slice(0, visibleList).map((item) => <div className="mi-mapa-saved-row" key={item.id}>
            <button className={`mi-mapa-item ${selected === item.id ? "selected" : ""}`}
              type="button" onClick={() => {
                setSelected(item.id);
                setView("mapa");
                const index = saved.indexOf(item);
                if (index >= visibleTracks) setVisibleTracks(index + 1);
              }}>
              <strong>{item.routeId && routeNames[item.routeId] ? routeNames[item.routeId] : item.name}</strong>
              <small>{fechaEcuador(item.startedAt)} · {(item.distanceM / 1000).toFixed(1)} km · {formatDuration(item.durationS)}</small>
            </button>
            <button type="button" className="mi-mapa-remove" disabled={savingId === item.id}
              onClick={() => void changeSaved(item, false)} aria-label={`Quitar ${item.name} de Mi mapa`}>Quitar</button>
          </div>)}

          {saved.length > visibleList && <button type="button" onClick={() => setVisibleList((count) => count + 30)}>Ver 30 salidas mas</button>}

          <details className="mi-mapa-add-previous">
            <summary>Agregar salidas anteriores ({candidates.length})</summary>
            {candidates.slice(0, visiblePrevious).map((item) => <div className="mi-mapa-candidate" key={item.id}>
              <span><strong>{item.routeId && routeNames[item.routeId] ? routeNames[item.routeId] : item.name}</strong>
                <small>{fechaEcuador(item.startedAt)} · {(item.distanceM / 1000).toFixed(1)} km</small></span>
              <button type="button" disabled={savingId === item.id} onClick={() => void changeSaved(item, true)}>
                {savingId === item.id ? "Guardando..." : "Guardar"}
              </button>
            </div>)}
            {candidates.length > visiblePrevious && <button type="button" onClick={() => setVisiblePrevious((count) => count + 30)}>Ver 30 anteriores mas</button>}
          </details>
        </details>
        </aside>
      </div>
    </section>
  );
}
